import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import TableStore from "@harbor/elements/utils/table/store";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import AppHOC from "../../../generics/AppHOC";
import OverLappingSpinner from "../../../common/Spinner/OverLappingSpinner";
import SettingsDrawer from "../../../common/SettingsDrawer";
import NoData from "../../../common/table/NoData";
import SiteFilter from "./siteFilter";
import { useMount, toast } from "../../../utils/genericCommon";
import { downloadAsCSVFile } from "../../../utils/common";
import { getTableColumns, setSitesData } from "./tableConfig";
import apiService from "../../../config/api-config";
import { useI18n } from "../../../i18n/useI18n";
import { SITES_TABLE_DEFAULT_TGL, LOCAL_STORAGE_LABELS } from "../../../utils/enums";
import { useTableSettings } from "../../../utils/table";
import { shortDisplayDate24HourWithSec, formatDate } from "../../../utils/displayTime";
import { timeFilterTypesCustomV4 } from "../../../utils/enums";


const [
  HbrInput,
  HbrIcon,
  HbrButton,
  HbrSpinner,
  HbrTable,
  HbrPagination
] = reactWrapper([
  "hbr-input",
  "hbr-icon",
  "hbr-button",
  "hbr-spinner",
  "hbr-table",
  "hbr-pagination"
]);
// default sort column and sort order
let defaultSort = [['availability', 'desc']]; 
const tableId = "sites_table";

const Table = ({ globalFilter, setSelectedSite, reporting, siteSummary }) => {
  const { tViews } = useI18n('views');
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [exportLoader, setExportLoader] = useState(false);
  const [, forceUpdate] = useState(null); // Used to trigger re-render from TableStore
  const mount = useMount(); // Custom hook to track mount status
  const dataRef = useRef({ timestamp: 0, error: null,availabilityStatus: "all" }); // Track fetch timestamp and error

  const openTableSettings = () => {
    tableStore.setState({ settingsOpen: true });
  }

  const columnsHook = useColumns({
      config: ({store}) => getTableColumns(tableId, navigate, reporting, setSelectedSite, openTableSettings, store),
      order: defaultSort
  });

  const tableStore = useMemo(() => {
    const store = new TableStore({
      query: { page: 0, pageLength: 30 },
      hooks: [columnsHook],
      fetchData: async ({query, setSource, setCount}) => {
        const { page, pageLength, search, order } = query;
        const timestamp = Date.now();
        let v4Payload;

        if(reporting){
          //Set payload for reporting
          const current_period_start = siteSummary.reportOptions.current_period_start;
          const current_period_end = siteSummary.reportOptions.current_period_end;
          const time_range = siteSummary.reportOptions.timeRange;

          v4Payload = {
            time_frame: timeFilterTypesCustomV4[time_range],
            entry_ts: {
              start: shortDisplayDate24HourWithSec(formatDate(current_period_start).utc()),
              end: shortDisplayDate24HourWithSec(formatDate(current_period_end).utc())
              }
          }
        } else {
          v4Payload = {
            entry_ts: globalFilter.globalV4Payload.entry_ts,
            time_frame: globalFilter.globalV4Payload.time_frame
          }
        }
        const payload = {
          ...v4Payload,
          size: pageLength,
          offset: page * pageLength,
          sort: Object.fromEntries(order.map(([key, value]) => [key.replace(/display_/g, ''),value]))
        };
        if (dataRef.current.availabilityStatus !== "all") {
          payload.availability_status = [dataRef.current.availabilityStatus];
        }
        if (search) payload.search = search;
        dataRef.current.timestamp = timestamp;
        setLoader(true); 

        try {
          const res = await apiService.getSitesTableList(payload);
          if (mount.mounted === true && timestamp === dataRef.current.timestamp) {
            const { count, errorObject } = res?.data || {}; 
            if (errorObject) {
              dataRef.current.error = errorObject;
              setSource([]);
              setCount(0, false);
            }else {
              // set sites data 
              const tableData = setSitesData(res.data.data);           
              // set table data
              setSource(tableData);
              setCount(count || tableData.length, false);
            }
          }
        } catch (error) {
          console.error("Error fetching sites table data:", error);
          dataRef.current.error = error;
          setSource([]);
          setCount(0, false);
        } finally {
          setLoader(false);
        }
      }
    });
    useTableSettings(LOCAL_STORAGE_LABELS.SITES_TABLE, SITES_TABLE_DEFAULT_TGL, columnsHook, store)
    return store;
  }, [
    globalFilter.timeFilter.current_period[0],
    globalFilter.timeFilter.current_period[1],
    globalFilter.currentTimeStamp,
    globalFilter.selectedSite
  ])

  // Subscribe to TableStore updates for re-render
  useEffect(() => {
    const unsubscribe = tableStore.subscribe(forceUpdate);
    return () => unsubscribe();
  }, [tableStore]);

  // Destructure table state
  const { source, columns, query, count, settingsOpen } = tableStore.getState();
  const { search, pageLength, page, order } = query;

  /**
   * Handle search input and update table query.
   * Supports both blur and Enter key events.
   */
  const onSearch = useCallback(
    (event) => {
      const value = event?.target?.value ?? "";
      if (value !== search) tableStore.setQuery({ search: value, page: 0 });
    },
    [search, tableStore]
  );

  // load table data on qoe status selection
  const onavailabilityStatusChange = event => {
    dataRef.current.availabilityStatus = event.target.value;
    tableStore.setQuery({ page: 0 });
  };

  const getExportData  = async () => {
    // get payload
    let v4Payload;
    if(reporting){
      //Set payload for reporting
      const current_period_start = siteSummary.reportOptions.current_period_start;
      const current_period_end = siteSummary.reportOptions.current_period_end;
      const time_range = siteSummary.reportOptions.timeRange;

      v4Payload = {
        time_frame: timeFilterTypesCustomV4[time_range],
        entry_ts: {
          start: shortDisplayDate24HourWithSec(formatDate(current_period_start).utc()),
          end: shortDisplayDate24HourWithSec(formatDate(current_period_end).utc())
          }
      }
    } else {
      v4Payload = {
        entry_ts: globalFilter.globalV4Payload.entry_ts,
        time_frame: globalFilter.globalV4Payload.time_frame
      }
    }
    if (dataRef.current.availabilityStatus !== "all") {
      v4Payload.availability_status = [dataRef.current.availabilityStatus];
    }
    const payload = {
      ...v4Payload,
      sort: Object.fromEntries(order.map(([key, value]) => [key.replace(/display_/g, ''),value]))
    };
    if (search !== "") payload.search = search;
    setExportLoader(true);
    try {
      const res = await apiService.getSitesTableList(payload);
      if (mount.mounted) {
        if (
          !(res.errorObject instanceof Object)
          && res.data instanceof Object
          && Array.isArray(res.data.data)
        ) {
          const resData = res.data.data;
          const newColumns = columns.filter(col => col.name !== "Actions" && col.prop !== "actions"); // Remove selection column
          const tableData = setSitesData(resData);
          const columnTitles = newColumns.map(item => `"${item.name}"`);
          const data = [columnTitles.join(",")];
          const columnKeys = newColumns.map(item => item.prop);

          for (const item of tableData) {
            const row = columnKeys.map(key => `"${item[key]}"`);
            data.push(row.join(","));
          }
          // download the csv file
          downloadAsCSVFile(data.join("\n"), tViews('allSiteSitesLabel'));
          } else {
          toast(tViews('exportError'), "danger");
        }
      }
    } catch (error) {
      toast(tViews('exportError'), "danger");
    } finally {
      setExportLoader(false);
    }
  }; 
  return (
    <div data-cy="applicationTableView">
    <div className="table-container hbr-css__layout-col-md">
      {loader && <OverLappingSpinner />}
      <div className="hbr-css__layout-col-md">
        <div className="hbr-css__text-heading-lg-bold" role="heading" aria-level={'2'}>
          {tViews('allSiteSitesLabel')}
        </div>
        {!reporting && (
        <> 
        <SiteFilter
          value={dataRef.current.availabilityStatus}
          onChange={onavailabilityStatusChange}
          loader={loader}
        />
        <div
          className="hbr-css__layout-row-xs hbr-css__layout-justify-between"
        >
          <div className="hbr-css__layout-row-xs">
            <HbrInput
              placeholder={tViews('searchPlaceholder')}
              aria-label={tViews('a11yLabels.searchInputAriaLabel')}
              clearable
              disabled={loader}
              value={search}
              onHbr-clear={() => onSearch()}
              data-testid="sites-table-search-input"
              role="searchbox"
              onkeydown={event => {
                if (event.code === "Enter") {
                  event.preventDefault();
                  if (!event.target._searchHandled) {
                    event.target._searchHandled = true;
                    onSearch(event);
                  }
                }
              }}
              onHbr-blur={event => {
                if (!event.target._searchHandled) {
                  onSearch(event);
                }
                event.target._searchHandled = false;
              }}
            >
              <HbrIcon
                slot="prefix"
                name="magnifying-glass"
                sentiment="neutral"
                aria-hidden="true"
              />
            </HbrInput>
            <div className="hbr-css__filter-description">
              {tViews('numberOfSites', { count: count})}
            </div>
          </div>
          {(Number.isFinite(count) && count > 0) && <HbrButton
            variant="outline"
            onClick={getExportData}
            disabled={loader || exportLoader}
            aria-busy={exportLoader}
            data-testid="hbr-export-button"
          >
            {exportLoader ? (
              <HbrSpinner slot="prefix" aria-hidden="true" />
            ) : (
              <HbrIcon slot="prefix" name="upload-simple" aria-hidden="true" />
            )}
            {tViews('export')}
          </HbrButton>}
        </div>
        </>
        )}
      </div>
      <HbrTable
        id={tableId}
        data-testid={tableId}
        columns={columns}
        readonly
        resize
        className={reporting ? 'reportSitesTable' : 'allSitesTable'}
        frameSize={source.length}
        serverSide
        externalSorting
        canFocus={false}
        useClipboard={false}
        headerSlot={true}
        role="table"
        aria-label={tViews('siteNameLabel')}
        onHbr-before-sort={event => {
          const { column, order } = event.detail;
          const columnsHook = tableStore.getHook("columns");
          if (order) columnsHook.sort({ order: [[column.id, order]] });
          else if (column.id === defaultSort[0][0])
            columnsHook.sort({ order: [[column.id, "asc"]] });
          else columnsHook.sort({ order: defaultSort });
        }}
        source={source}
      >
        <NoData
          count={source.length}
          loader={loader}
          error={dataRef.current.error}
        />
      </HbrTable>
      <HbrPagination
        className={reporting === true ? 'reportPagination' : ''}
        orderArray={[1, 2, 0]}
        showAll
        disabled={loader}
        tableStore={tableStore}
        pageSize={pageLength}
        itemCount={count}
        page={page}
        size="small"
        aria-label={tViews('a11yLabels.paginationAriaLabel')}
        onHbr-page-changed={event => {
          const index = Number.isInteger(event.detail) ? event.detail : 0;
          tableStore.setQuery({ page: index });
        }}
        onHbr-size-changed={event => {
          tableStore.setQuery({ pageLength: event.detail.pageSize, page: 0 });
        }}
      />        
    </div>
    {settingsOpen && <SettingsDrawer
      tableStore={tableStore}
      localStorageName={LOCAL_STORAGE_LABELS.SITES_TABLE}
      defaultToggles={SITES_TABLE_DEFAULT_TGL}
      dataTestid="sites_table"
    />}
    </div>
  );
};
 

Table.propTypes = {
  globalFilter: PropTypes.object.isRequired,  
  setSelectedSite:  PropTypes.func,
  reporting: PropTypes.bool,
  siteSummary: PropTypes.object
}; 

export default AppHOC(Table);
